import React from 'react';
import Decimal from 'decimal.js';

import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  propTypes,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_VOUCHER,
  LINE_ITEM_WALLET_CREDIT,
} from '../../util/types';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * Calculates the total price in sub units for multiple line items.
 */
const lineItemsTotal = (lineItems, marketplaceCurrency) => {
  const amount = lineItems.reduce((total, item) => {
    return total.plus(item.lineTotal.amount);
  }, new Decimal(0));
  const currency = lineItems[0] ? lineItems[0].lineTotal.currency : marketplaceCurrency;
  // CRITICAL FIX: Convert Decimal to number for Money constructor
  return new Money(amount.toNumber(), currency);
};

/**
 * Checks if line item represents commission
 */
const isCommission = lineItem => {
  return (
    lineItem.code === LINE_ITEM_PROVIDER_COMMISSION ||
    lineItem.code === LINE_ITEM_CUSTOMER_COMMISSION
  );
};

/**
 * Checks if line item is a discount (voucher or wallet credit)
 */
const isDiscount = lineItem => {
  return (
    lineItem.code === LINE_ITEM_VOUCHER ||
    lineItem.code === LINE_ITEM_WALLET_CREDIT
  );
};

/**
 * Returns non-commission, reversal line items
 */
const nonCommissionReversalLineItems = lineItems => {
  return lineItems.filter(item => !isCommission(item) && item.reversal);
};

/**
 * Returns non-reversal line items that make up the subtotal
 * (excludes commissions, discounts, and reversals)
 */
const getSubtotalLineItems = lineItems => {
  return lineItems.filter(item => !isCommission(item) && !isDiscount(item) && !item.reversal);
};

const LineItemRefundMaybe = props => {
  const { lineItems, intl, marketplaceCurrency, isCustomer } = props;

  // all non-commission, reversal line items
  const refundLineItems = nonCommissionReversalLineItems(lineItems);
  
  // For customers, also include customer commission refund to consolidate
  const customerCommissionRefund = isCustomer 
    ? lineItems.find(item => item.code === LINE_ITEM_CUSTOMER_COMMISSION && item.reversal)
    : null;

  // If there's a customer commission refund, add it to the total
  const allRefundItems = customerCommissionRefund 
    ? [...refundLineItems, customerCommissionRefund]
    : refundLineItems;

  let refund;
  
  if (refundLineItems.length > 0) {
    if (isCustomer) {
      // Customer view: sum all reversal items including customer commission
      refund = lineItemsTotal(allRefundItems, marketplaceCurrency);
    } else {
      // Provider view: calculate as negative of subtotal
      // This ensures provider sees base amount (sessions + seasonal + promo)
      // NOT the discounted amount customer paid (after voucher/wallet)
      const subtotalItems = getSubtotalLineItems(lineItems);
      const subtotal = lineItemsTotal(subtotalItems, marketplaceCurrency);
      // Negate the subtotal to show as refund
      refund = new Money(-subtotal.amount, subtotal.currency);
    }
  } else {
    refund = new Money(0, marketplaceCurrency);
  }

  const formattedRefund = allRefundItems.length > 0 ? formatMoney(intl, refund, true) : null;

  return formattedRefund ? (
    <div className={css.lineItem}>
      <span className={css.itemLabel}>
        <FormattedMessage id="BookingBreakdown.refund" />
      </span>
      <span className={css.itemValue}>{formattedRefund}</span>
    </div>
  ) : null;
};

LineItemRefundMaybe.propTypes = {
  lineItems: propTypes.lineItems.isRequired,
  intl: intlShape.isRequired,
};

export default LineItemRefundMaybe;
